import React from 'react';
import { ChakraProvider, Box, extendTheme, Flex } from '@chakra-ui/react';
import { Routes, Route } from 'react-router-dom';
import FormPage from './components/FormPage';
import Impressum from './components/Impressum';
import './App.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const queryClient = new QueryClient();

// Custom theme with ABC Favorit fonts, custom colors, and sharp corners
const theme = extendTheme({
  fonts: {
    heading: `'ABC Favorit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    body: `'ABC Favorit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  },
  colors: {
    brand: {
      50: '#e6f2ff',
      100: '#b3d9ff',
      200: '#80bfff',
      300: '#4da6ff',
      400: '#1a8cff',
      500: '#005cff', // Button color
      600: '#0047cc',
      700: '#003399',
      800: '#001e66',
      900: '#000a33',
    },
    text: {
      primary: '#1c2963', // Main text color
    },
  },
  fontSizes: {
    heading: '2.47593rem',
    description: '0.990371rem',
    caption: '0.990371rem',
    component: '1.37552rem',
    text: '0.99037rem',
  },
  radii: {
    none: '0',
    sm: '0',
    base: '0',
    md: '0',
    lg: '0',
    xl: '0',
    '2xl': '0',
    '3xl': '0',
    full: '0',
  },
  components: {
    Input: {
      defaultProps: {
        variant: 'flushed',
        focusBorderColor: '#aaa6ff',
      },
      baseStyle: {
        field: {
          color: '#1c2963',
          fontSize: '1.2rem',
          fontWeight: '400',
          borderBottomColor: '#2d3748',
          borderBottomWidth: '2px',
          _placeholder: {
            color: '#aaa6ff',
            opacity: 0.8,
            fontSize: '1.37552rem',
            fontStyle: 'italic',
          },
          _focus: {
            borderBottomColor: '#aaa6ff',
            borderBottomWidth: '2px',
            boxShadow: 'none',
          },
        },
      },
    },
    Textarea: {
      defaultProps: {
        variant: 'flushed',
        focusBorderColor: '#aaa6ff',
      },
      baseStyle: {
        color: '#1c2963',
        fontSize: '1.2rem',
        fontWeight: '400',
        borderBottomColor: '#2d3748',
        borderBottomWidth: '2px',
        _placeholder: {
          color: '#aaa6ff',
          opacity: 0.8,
          fontSize: '1.37552rem',
          fontStyle: 'italic',
        },
        _focus: {
          borderBottomColor: '#aaa6ff',
          borderBottomWidth: '2px',
          boxShadow: 'none',
        },
      },
    },
    Select: {
      defaultProps: {
        variant: 'flushed',
        focusBorderColor: '#aaa6ff',
      },
      baseStyle: {
        field: {
          bg: 'transparent',
        },
      },
    },
    FormLabel: {
      baseStyle: {
        color: '#1c2963',
        fontSize: '1.1rem',
        fontWeight: '600',
        mb: 3,
        letterSpacing: '-0.025em',
      },
    },
    Checkbox: {
      defaultProps: {
        colorScheme: 'brand',
        size: 'lg',
      },
      baseStyle: {
        control: {
          borderRadius: 'md',
          borderWidth: '2px',
          _hover: {
            borderColor: '#aaa6ff',
          },
          _focus: {
            boxShadow: '0 0 0 2px rgba(170, 166, 255, 0.2)',
          },
        },
        label: {
          color: '#1c2963',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
    },
    Button: {
      defaultProps: {
        colorScheme: 'brand',
      },
      baseStyle: {
        borderRadius: 'lg',
        color: 'white',
        fontWeight: '600',
        _hover: {
          transform: 'translateY(-1px)',
        },
        _active: {
          transform: 'translateY(0px)',
        },
        transition: 'all 0.2s ease',
      },
    },
    Box: {
      baseStyle: {
        borderRadius: '0',
      },
    },
    Container: {
      baseStyle: {
        color: '#1c2963',
      },
    },
    Text: {
      baseStyle: {
        color: '#1c2963',
        fontSize: '0.99037rem',
      },
    },
    Heading: {
      baseStyle: {
        color: '#1c2963',
        letterSpacing: '-0.025em',
      },
    },
    Alert: {
      baseStyle: {
        container: {
          borderRadius: 'lg',
          border: '1px solid',
        },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={theme}>
        <Flex direction="column" minH="100vh">
          <Box flex="1" bg="white">
            <Routes>
              <Route path=":id" element={<FormPage />} />
              <Route path="/impressum" element={<Impressum />} />
            </Routes>
          </Box>

        </Flex>
      </ChakraProvider>
      {/* Devtools only in development */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
